import { FormField, UserPicker, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired } from '../model';
import { useDelegateMember, useOrganisationPeople, useSpaceMembers } from './api';
import {
  withoutKey,
  EMPTY_LIMITED_DRAFT,
  WHOLE_SCOPE_DRAFT,
  isRights,
  isWholeScope,
  rightsUpTo,
  scopeOfDraft,
  sharingErrorMessage,
  type Partnership,
  type Rights,
  type ScopeDraft,
} from './model';
import { ScopePicker } from './scope-picker';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"}});

export interface AddDelegateDialogProps {
  spaceId: string;
  /** The partner organisation: its own members are the people that can be added. */
  orgId: string;
  partnership: Partnership;
  open: boolean;
  onClose: () => void;
}

/** What a delegate gets by default: the partnership's rights, never above "write" unprompted. */
const defaultRights = (max: Rights): Rights => (max === 'approve' ? 'write' : max);

function AddDelegateForm({
  spaceId,
  orgId,
  partnership,
  onClose,
}: Omit<AddDelegateDialogProps, 'open'>) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const delegate = useDelegateMember(spaceId);
  const people = useOrganisationPeople(orgId);
  const delegates = useSpaceMembers(spaceId, partnership.id);

  const whole = isWholeScope(partnership.scope);
  const remaining = Math.max(0, partnership.memberCap - partnership.activeMembers);
  const alreadyIn = new Set((delegates.data?.items ?? []).map((member) => member.userId));
  const options = (people.data?.items ?? [])
    .filter((person) => !alreadyIn.has(person.userId))
    .map((person) => ({ id: person.userId, label: person.displayName }));

  const [userIds, setUserIds] = useState<string[]>([]);
  const [scope, setScope] = useState<ScopeDraft>(whole ? WHOLE_SCOPE_DRAFT : EMPTY_LIMITED_DRAFT);
  const [rights, setRights] = useState<Rights>(defaultRights(partnership.rights));
  const [errors, setErrors] = useState<{ people?: string; scope?: string }>({});
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (delegate.isPending) return;
    setFailure(null);
    const next: { people?: string; scope?: string } = {};
    if (userIds.length === 0) next.people = t('sharing.delegates.add.errors.peopleRequired');
    else if (userIds.length > remaining) {
      next.people = t('sharing.delegates.add.errors.overCap', { remaining });
    }
    const body = scopeOfDraft(scope);
    if (body === null) next.scope = t('sharing.scope.errors.empty');
    setErrors(next);
    if (next.people || next.scope || body === null) return;
    let added = 0;
    try {
      // One delegation per person, in order: the first refusal stops the others.
      for (const userId of userIds) {
        await delegate.mutateAsync({
          spaceId,
          partnerId: partnership.id,
          data: { userId, scope: body, rights },
        });
        added += 1;
      }
      notify({ intent: 'success', message: t('sharing.delegates.add.done') });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      // The people already added are out of the list: only the others remain to choose.
      setUserIds(userIds.slice(added));
      setFailure(
        sharingErrorMessage(t, tCommon, error, {
          forbidden: 'sharing.delegates.add.errors.forbidden',
        }),
      );
    }
  };

  return (
    <form
      noValidate
      aria-label={t('sharing.delegates.add.title')}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <DialogBody>
        <DialogTitle>{t('sharing.delegates.add.title')}</DialogTitle>
        <DialogContent>
          <div className={styles.form}>
            <p className={styles.intro}>
              {t('sharing.delegates.add.intro', {
                active: partnership.activeMembers,
                cap: partnership.memberCap,
              })}
            </p>
            <FormField
              required
              label={t('sharing.delegates.add.people')}
              hint={t('sharing.delegates.add.peopleHint', { name: isolate(partnership.host.name) })}
              error={errors.people}
            >
              <UserPicker
                label={t('sharing.delegates.add.people')}
                options={options}
                value={userIds}
                onChange={(ids) => {
                  setUserIds(ids);
                  setErrors((current) => withoutKey(current, 'people'));
                }}
              />
            </FormField>

            <ScopePicker
              spaceId={spaceId}
              allowWhole={whole}
              within={isWholeScope(partnership.scope) ? null : partnership.scopeItems}
              value={scope}
              error={errors.scope ?? null}
              onChange={(value) => {
                setScope(value);
                setErrors((current) => withoutKey(current, 'scope'));
              }}
            />

            <FormField label={t('sharing.rightsOption.label')}>
              <RadioGroup
                value={rights}
                onChange={(_event, data) => {
                  if (isRights(data.value)) setRights(data.value);
                }}
              >
                {rightsUpTo(partnership.rights).map((value) => (
                  <Radio key={value} value={value} label={t(`sharing.rightsOption.${value}`)} />
                ))}
              </RadioGroup>
            </FormField>

            {failure ? (
              <MessageBar intent="error" layout="multiline">
                <MessageBarBody>{failure}</MessageBarBody>
              </MessageBar>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" appearance="secondary" onClick={onClose}>
            {tCommon('actions.cancel')}
          </Button>
          <Button type="submit" appearance="primary" disabledFocusable={delegate.isPending}>
            {delegate.isPending
              ? t('sharing.delegates.add.submitting')
              : t('sharing.delegates.add.submit')}
          </Button>
        </DialogActions>
      </DialogBody>
    </form>
  );
}

/**
 * "Ajouter un membre": the partner admin picks people of its own organisation and gives them
 * access to the space: within the partnership's scope and rights, and the cap of people it may add.
 */
export function AddDelegateDialog({ open, onClose, ...rest }: AddDelegateDialogProps) {
  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <AddDelegateForm {...rest} onClose={onClose} />
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/add-delegate-dialog.griffel.css";